/**
 * VideoStudioApp browser commands (creation + thin tool wrappers).
 *
 * Mirrors the DatabaseApp pattern:
 *   - videoproject_create: sidebar-friendly creation command. Creates a
 *     VideoProject entity directly via entityStore so the sidebar's
 *     inline-rename flow kicks in without a tool-runtime round trip. When
 *     called from AI chat with a name, delegates to the tool-runtime handler.
 *   - video_*: thin wrappers that forward to tool-runtime via callToolRuntime.
 *     The tool-runtime is the authoritative path (it broadcasts progress and
 *     persists results via backend), so these wrappers keep parameters in
 *     sync with the canonical MANIFESTs in tools/*.m.ts.
 */

import type { SelfContainedCommand, CommandExecutionContext } from '../../../types/commands';
import type { StandardToolResult } from '../../../types/standardToolResults';
import { callToolRuntime } from '../../../utils/callToolRuntime';
import { schemaToParameters } from '../../../utils/schemaToParameters';
import { createSuccessResult, createErrorResult } from '../../../types/standardToolResults';
import { vsLog } from '../services/videoStudioLogger';

function wrapTool(
  id: string,
  description: string,
  inputSchema: Record<string, any>,
): SelfContainedCommand {
  return {
    id,
    description,
    autonomyLevel: 'auto',
    visualTreatment: 'silent',
    schema: { parameters: schemaToParameters(inputSchema) },
    examples: { good: [], bad: [] },
    handler: async (
      params: any,
      context: CommandExecutionContext,
    ): Promise<StandardToolResult> => {
      vsLog.log(`[video_cmd] ${id}`, params);
      const enrichedContext = {
        ...context,
        spaceId: context?.spaceId || params?.spaceId || '',
      };
      return callToolRuntime(id, params, enrichedContext);
    },
  };
}

export const videoproject_create: SelfContainedCommand = {
  id: 'videoproject_create',
  description:
    'Create a new VideoProject (timeline document) with default tracks (V1 video, A1 audio).',
  autonomyLevel: 'auto',
  visualTreatment: 'silent',
  schema: {
    parameters: schemaToParameters({
      type: 'object',
      properties: {
        name: { type: 'string' },
        fps: { type: 'number', enum: [24, 25, 30, 48, 60], default: 30 },
        resolution: {
          type: 'object',
          properties: {
            w: { type: 'number' },
            h: { type: 'number' },
          },
        },
        folderId: { type: 'string' },
      },
      required: [],
    }),
  },
  examples: { good: [], bad: [] },
  handler: async (
    params: any,
    context: CommandExecutionContext,
  ): Promise<StandardToolResult> => {
    vsLog.log('[video_cmd] videoproject_create', params);

    const spaceId: string = context?.spaceId || params?.spaceId || '';
    const folderId: string | undefined = params?.folderId;
    const name: string = (params?.name || params?.title || '').trim() || 'New Video';

    // When called from AI with an explicit name, delegate to tool-runtime for
    // the full Y.Doc seeding + broadcast path (Phase F).
    if (params?.name?.trim()) {
      const enrichedContext = { ...context, spaceId };
      return callToolRuntime('videoproject_create', params, enrichedContext);
    }

    try {
      const { default: useEntityStore } = await import('../../../stores/entityStore');
      const store = useEntityStore.getState();

      const skeleton = {
        '@type': 'VideoProject' as const,
        name,
        fps: params?.fps ?? 30,
        resolution: params?.resolution ?? { w: 1920, h: 1080 },
        durationMs: 0,
        sourceEntityIds: [] as string[],
        updatedAt: new Date().toISOString(),
        ...(folderId ? { groupIds: [folderId] } : {}),
      };

      const entity = await store.actions.createEntity({
        entityType: 'VideoProject',
        name,
        spaceId,
        skeleton,
      } as any);

      const entityId = entity._id || (entity as any).id;
      vsLog.log('[video_cmd] videoproject_create ok', { entityId, name: entity.name });

      return createSuccessResult(`Created video project "${entity.name}"`, {
        entityId,
        projectId: entityId,
        llmId: (entity as any).llmId,
        name: entity.name,
      });
    } catch (err: any) {
      vsLog.error('[video_cmd] videoproject_create failed', err);
      return createErrorResult(
        'Failed to create video project',
        err?.message || 'Unknown error',
        'execution',
      );
    }
  },
};

// Thin wrappers for the tool-runtime video tools. Schemas intentionally match
// MANIFESTs in gui/src/apps/VideoStudioApp/tools/*.m.ts (Phase F).

const VIDEO_OP_SCHEMA = {
  type: 'object' as const,
  properties: {
    projectId: { type: 'string', description: 'VideoProject entity id' },
    clipId: { type: 'string', description: 'Timeline clip id (when scoped to a clip)' },
    sourceEntityId: { type: 'string', description: 'File.Video.* source entity id' },
    rangeIn: { type: 'number', description: 'Range start on timeline (ms)' },
    rangeOut: { type: 'number', description: 'Range end on timeline (ms)' },
    maskEntityId: { type: 'string', description: 'Mask video/JSON entity id' },
    providerHint: { type: 'string', description: "'replicate' | 'fal' | 'runway' | 'luma' | 'comfy_local' | 'topaz'" },
    params: { type: 'object', description: 'Operation-specific params' },
    opId: { type: 'string', description: 'Correlation id (client-generated when omitted)' },
    spaceId: { type: 'string' },
  },
  required: [],
};

export const video_upscale = wrapTool('video_upscale', 'Upscale a clip or range.', VIDEO_OP_SCHEMA);
export const video_denoise = wrapTool('video_denoise', 'Denoise a clip or range.', VIDEO_OP_SCHEMA);
export const video_deblur = wrapTool('video_deblur', 'Deblur a clip or range.', VIDEO_OP_SCHEMA);
export const video_stabilize = wrapTool('video_stabilize', 'Stabilize a clip or range.', VIDEO_OP_SCHEMA);
export const video_interpolate = wrapTool('video_interpolate', 'Frame-interpolate a clip or range.', VIDEO_OP_SCHEMA);
export const video_inpaint_region = wrapTool(
  'video_inpaint_region',
  'Inpaint/replace a masked region of a clip over a range.',
  VIDEO_OP_SCHEMA,
);
export const video_restyle = wrapTool('video_restyle', 'Restyle a clip or range via a provider v2v model.', VIDEO_OP_SCHEMA);
export const video_extend = wrapTool('video_extend', 'Extend a clip before/after with new frames.', VIDEO_OP_SCHEMA);
export const video_variation = wrapTool('video_variation', 'Generate a variation of a clip preserving motion/composition.', VIDEO_OP_SCHEMA);
export const video_generate_from_frame = wrapTool(
  'video_generate_from_frame',
  'Generate a new clip starting from a source-frame image reference.',
  VIDEO_OP_SCHEMA,
);
export const video_frame_export = wrapTool(
  'video_frame_export',
  'Export hero frame(s) from a clip as File.Image.PNG entities.',
  VIDEO_OP_SCHEMA,
);
export const video_mask_track = wrapTool(
  'video_mask_track',
  'Track a mask across a range (SAM2 or local alternative).',
  VIDEO_OP_SCHEMA,
);
export const video_export = wrapTool(
  'video_export',
  'Render a VideoProject timeline to an MP4 entity.',
  VIDEO_OP_SCHEMA,
);

export const VIDEO_STUDIO_PROJECT_COMMANDS = [
  videoproject_create,
  video_upscale,
  video_denoise,
  video_deblur,
  video_stabilize,
  video_interpolate,
  video_inpaint_region,
  video_restyle,
  video_extend,
  video_variation,
  video_generate_from_frame,
  video_frame_export,
  video_mask_track,
  video_export,
];
